// Referido.jsx — la página a la que lleva el enlace de un creador.
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { trackAppStoreClick, trackGooglePlayClick, trackCTAClick } from "../analytics/googleAnalytics";
import appStoreImg from "../assets/img/app-store.png";
import googlePlayImg from "../assets/img/google.store.png";
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  isIOS,
  isAndroid,
  getMobileStoreUrl,
} from "../utils/dispositivo";
import "./DownloadRedirect.css";
import "./Referido.css";

const LOGO = "/assets/img/wayo-logo.webp";

/**
 * El enlace que reparte un creador: `wayoai.app/r/SUCODIGO`.
 *
 * EL PROBLEMA QUE RESUELVE. Un seguidor ve un reel, va a la tienda, se instala
 * la app y en el onboarding elige «Instagram» en la pregunta de cómo nos
 * conoció. El campo del código de referido solo se destapa con «Amigo/Familia» o
 * «Mi entrenador/a», así que esa persona NO ve nunca dónde meterlo: el creador
 * no cobra y nosotros no sabemos qué reel funcionó.
 *
 * CÓMO SE ARREGLA SIN PODER HACER SEGUIMIENTO ENTRE LA WEB Y LA APP. Entre pulsar
 * aquí e instalar la app hay un salto por la tienda que borra cualquier rastro;
 * lo que sobrevive a ese salto es el portapapeles. Así que el botón hace dos
 * cosas a la vez: copia el código y abre la tienda. Al arrancar, la app mira el
 * portapapeles y lo rellena sola.
 *
 * ⚠️ La copia va DENTRO del onClick a propósito. `navigator.clipboard` exige un
 * gesto del usuario: hacerlo al cargar la página falla en silencio, y encima el
 * navegador de dentro de Instagram —por donde va a entrar casi todo el mundo— es
 * el más estricto con esto.
 */

/**
 * Los códigos son de 8 caracteres en mayúsculas, y el campo del onboarding está
 * limitado a 8 (`PasoReferidoPor.js`). Cortar aquí evita repartir enlaces con un
 * código que la app no va a dejar teclear entero.
 */
function limpiarCodigo(bruto) {
  return String(bruto || "").trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 8);
}

/**
 * Al portapapeles va con prefijo, y a la pantalla sin él. La app busca
 * exactamente `WAYO-XXXX` para no confundir con cualquier cosa que la persona
 * llevara copiada de antes, y quita el prefijo si alguien lo pega a mano.
 */
function paraElPortapapeles(codigo) {
  return `WAYO-${codigo}`;
}

export default function Referido() {
  // Sin useParams: esta página se monta también desde el prerender, donde no hay
  // router. La URL es la única fuente fiable en los dos sitios.
  const codigo = useMemo(() => {
    if (typeof window === "undefined") return "";
    const trozos = window.location.pathname.split("/").filter(Boolean);
    return limpiarCodigo(trozos[trozos.length - 1] === "r" ? "" : trozos[trozos.length - 1]);
  }, []);

  const [copiado, setCopiado] = useState(false);

  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const esMovil = isIOS(userAgent) || isAndroid(userAgent);

  // Si vuelve más tarde por su cuenta, el código sigue aquí. Es un apaño barato
  // para el que pulsa, se distrae y entra a la tienda media hora después.
  useEffect(() => {
    if (!codigo) return;
    try {
      window.localStorage.setItem("wayo_codigo_referido", codigo);
    } catch {
      // Modo incógnito o almacenamiento bloqueado: se sigue igual, solo se
      // pierde el recordatorio.
    }
  }, [codigo]);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(paraElPortapapeles(codigo));
      setCopiado(true);
      return true;
    } catch {
      // Sin permiso de portapapeles el código sigue en pantalla, grande, para
      // copiarlo a mano. Por eso nunca se esconde.
      return false;
    }
  }

  async function irATienda(destino, quien) {
    await copiar();
    trackCTAClick(`referido_${quien}`);
    if (quien === "ios") trackAppStoreClick("referido");
    if (quien === "android") trackGooglePlayClick("referido");
    // Un respiro para que se vea el "copiado" antes de que la tienda se lleve la
    // pantalla; sin esto el usuario no se entera de que lleva el código encima.
    setTimeout(() => { window.location.href = destino; }, 450);
  }

  if (!codigo) {
    return (
      <main className="download-page">
        <Helmet>
          <title>Invitación a Wayo</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="download-hero">
          <div className="download-brand">
            <img src={LOGO} alt="" />
            <span>Wayo</span>
          </div>
          <h1 className="ref-titulo">Este enlace no trae ningún código</h1>
          <p className="ref-sub">Puedes descargar la app igualmente.</p>
          <div className="ref-tiendas">
            <a href={APP_STORE_URL}><img src={appStoreImg} alt="Descargar en App Store" /></a>
            <a href={GOOGLE_PLAY_URL}><img src={googlePlayImg} alt="Disponible en Google Play" /></a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="download-page">
      <Helmet>
        <title>Te han invitado a Wayo · 7 días gratis</title>
        {/* Un enlace por creador no pinta nada en Google, y si se indexan
            acabamos con cientos de páginas casi idénticas compitiendo entre sí. */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="download-hero">
        <div className="download-brand">
          <img src={LOGO} alt="" />
          <span>Wayo</span>
        </div>

        <h1 className="ref-titulo">Te han invitado a probar Wayo</h1>
        <p className="ref-sub">
          Le haces una foto a lo que comes y Wayo se encarga del resto.
          Con esta invitación tienes <strong>7 días gratis</strong>.
        </p>

        <div className="ref-codigo-caja">
          <span className="ref-codigo-label">Tu código</span>
          <span className="ref-codigo">{codigo}</span>
          <button type="button" className="ref-copiar" onClick={copiar}>
            {copiado ? "✓ Copiado" : "Copiar"}
          </button>
        </div>

        <p className="ref-aviso">
          No tienes que apuntarlo: la app lo reconoce sola al crear tu cuenta.
          Si te lo pidiera, está en el paso «¿Cómo nos conociste?».
        </p>

        {esMovil ? (
          <button
            type="button"
            className="ref-cta"
            onClick={() => irATienda(getMobileStoreUrl(userAgent), isIOS(userAgent) ? "ios" : "android")}
          >
            Copiar código y descargar Wayo
          </button>
        ) : (
          <div className="ref-tiendas">
            <button type="button" className="ref-tienda-btn" onClick={() => irATienda(APP_STORE_URL, "ios")}>
              <img src={appStoreImg} alt="Descargar en App Store" />
            </button>
            <button type="button" className="ref-tienda-btn" onClick={() => irATienda(GOOGLE_PLAY_URL, "android")}>
              <img src={googlePlayImg} alt="Disponible en Google Play" />
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
